import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import leadsService from '../../services/leadsService';
import employeeService from '../../services/employeeService';
import {
  Employee,
  LEAD_ALERT_COLORS,
  LEAD_ALERT_LABELS,
  LEAD_CHANNEL_LABELS,
  LEAD_STATUS_COLORS,
  LEAD_STATUS_LABELS,
  Lead,
  LeadAlert,
  LeadChannel,
  LeadStatus,
} from '../../types';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { getStoreLogoUrl } from '../../config/storeBranding';
import LeadFormModal, { LeadFormValues } from './LeadFormModal';
import LeadOverviewDialog from './LeadOverviewDialog';
import LeadsReportPDF from './LeadsReportPDF';
import { buildLeadReportChartImages } from './leadChartImages';
import { buildLeadReportSummary } from './leadReportUtils';
import {
  buildLeadWhatsappMessage,
  calcDaysWithoutContact,
  formatDateBR,
  formatPhoneBR,
  getWhatsappLink,
  onlyDigits,
  resolveLeadAlert,
} from './leadUtils';

type StatusFilter = LeadStatus | 'all';
type AlertFilter = LeadAlert | 'all';
type OriginFilter = LeadChannel | 'all';
type SellerFilter = number | 'all';

const StatCard: React.FC<{ label: string; value: string | number; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Paper sx={{ p: 1.75, borderRadius: 2, height: '100%' }} variant="outlined">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.25 }}>
      {value}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {hint}
      </Typography>
    ) : null}
  </Paper>
);

const LeadsList: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useAuthUser();

  const [items, setItems] = useState<Lead[]>([]);
  const [sellers, setSellers] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [alertFilter, setAlertFilter] = useState<AlertFilter>('all');
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');
  const [sellerFilter, setSellerFilter] = useState<SellerFilter>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [overviewLead, setOverviewLead] = useState<Lead | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const sellerMap = useMemo(() => {
    const map = new Map<number, string>();
    sellers.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [sellers]);

  const sellerNameById = useCallback(
    (id: number | null | undefined) => {
      if (id == null) return '—';
      return sellerMap.get(id) || `ID ${id}`;
    },
    [sellerMap]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [leads, employees] = await Promise.all([
        leadsService.list(),
        employeeService.getAllEmployees().catch(() => [] as Employee[]),
      ]);
      setItems(leads);
      setSellers(
        ((employees || []) as Employee[]).filter(
          (e: Employee) => (e.role === 'vendedor' || e.role === 'gerente') && e.active !== false
        )
      );
    } catch (e) {
      console.error(e);
      setError('Não foi possível carregar os leads. Verifique se a API está disponível.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = onlyDigits(q);
    return items.filter((lead) => {
      if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
      if (originFilter !== 'all' && lead.origin !== originFilter) return false;
      if (sellerFilter !== 'all' && lead.seller_id !== sellerFilter) return false;
      const alert = resolveLeadAlert(lead);
      if (alertFilter !== 'all' && alert !== alertFilter) return false;
      if (!q) return true;
      return (
        lead.name.toLowerCase().includes(q) ||
        (lead.email || '').toLowerCase().includes(q) ||
        (lead.vehicle_interest || '').toLowerCase().includes(q) ||
        (qDigits && onlyDigits(lead.whatsapp).includes(qDigits))
      );
    });
  }, [items, search, statusFilter, alertFilter, originFilter, sellerFilter]);

  const summary = useMemo(
    () => buildLeadReportSummary(filtered, sellerNameById),
    [filtered, sellerNameById]
  );

  const filterLabel = useMemo(() => {
    const parts: string[] = [];
    if (statusFilter !== 'all') parts.push(`Status: ${LEAD_STATUS_LABELS[statusFilter]}`);
    if (sellerFilter !== 'all') parts.push(`Vendedor: ${sellerNameById(sellerFilter)}`);
    if (alertFilter !== 'all') parts.push(`Alerta: ${LEAD_ALERT_LABELS[alertFilter]}`);
    if (originFilter !== 'all') parts.push(`Origem: ${LEAD_CHANNEL_LABELS[originFilter]}`);
    if (search.trim()) parts.push(`Busca: "${search.trim()}"`);
    return parts.length ? parts.join(' • ') : 'Todos os leads (filtros atuais)';
  }, [statusFilter, sellerFilter, alertFilter, originFilter, search, sellerNameById]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (lead: Lead) => {
    setOverviewLead(null);
    setEditing(lead);
    setFormOpen(true);
  };

  const handleSubmit = async (payload: LeadFormValues) => {
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    try {
      if (editing?.id) {
        const updated = await leadsService.update(editing.id, payload);
        setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        setSnackbarMessage('Lead atualizado.');
      } else {
        const created = await leadsService.create(payload);
        setItems((prev) => [created, ...prev]);
        setSnackbarMessage('Lead criado.');
      }
      setSnackbarOpen(true);
      setFormOpen(false);
      setEditing(null);
    } catch (e) {
      console.error(e);
      setError('Não foi possível salvar o lead.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const reportSummary = buildLeadReportSummary(filtered, sellerNameById);
      const charts = buildLeadReportChartImages(reportSummary);
      const logoUrl = getStoreLogoUrl();
      const doc = (
        <LeadsReportPDF
          leads={filtered}
          summary={reportSummary}
          charts={charts}
          generatedAtLabel={generatedAtLabel}
          logoUrl={logoUrl}
          filterLabel={filterLabel}
          sellerNameById={sellerNameById}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${now.toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError('Não foi possível gerar o PDF do relatório.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Leads
          </Typography>
          <Button
            variant="outlined"
            startIcon={downloadingPdf ? <CircularProgress size={18} /> : <PdfIcon />}
            onClick={() => void handleDownloadPdf()}
            disabled={downloadingPdf}
            sx={{ borderRadius: 2 }}
          >
            {downloadingPdf ? 'Gerando PDF…' : 'Relatório PDF'}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: 2 }}>
            Atualizar
          </Button>
          {canWrite ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
              Novo lead
            </Button>
          ) : null}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Total (filtro)" value={summary.total} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Ganhos" value={summary.won} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Perdidos" value={summary.lost} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Conversão"
              value={summary.conversionRate == null ? '—' : `${summary.conversionRate}%`}
              hint="Ganhos ÷ (ganhos + perdidos)"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Alertas"
              value={summary.alertsAttention + summary.alertsUrgent}
              hint={`${summary.alertsUrgent} urgente(s)`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Média dias s/ contato"
              value={summary.daysAvgWithoutContact == null ? '—' : summary.daysAvgWithoutContact}
            />
          </Grid>
        </Grid>

        {summary.byStatus.some((s) => s.count > 0) ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {summary.byStatus
              .filter((s) => s.count > 0)
              .map((s) => (
                <Chip
                  key={s.status}
                  size="small"
                  label={`${s.label}: ${s.count}`}
                  color={LEAD_STATUS_COLORS[s.status]}
                  variant="outlined"
                  onClick={() => setStatusFilter(s.status)}
                />
              ))}
          </Stack>
        ) : null}

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar por nome, WhatsApp, e-mail ou veículo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((status) => (
                  <MenuItem key={status} value={status}>
                    {LEAD_STATUS_LABELS[status]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                select
                fullWidth
                label="Vendedor"
                value={sellerFilter}
                onChange={(e) =>
                  setSellerFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
                }
              >
                <MenuItem value="all">Todos</MenuItem>
                {sellers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                select
                fullWidth
                label="Alerta"
                value={alertFilter}
                onChange={(e) => setAlertFilter(e.target.value as AlertFilter)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {(Object.keys(LEAD_ALERT_LABELS) as LeadAlert[]).map((a) => (
                  <MenuItem key={a} value={a}>
                    {LEAD_ALERT_LABELS[a]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                select
                fullWidth
                label="Origem"
                value={originFilter}
                onChange={(e) => setOriginFilter(e.target.value as OriginFilter)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {(Object.keys(LEAD_CHANNEL_LABELS) as LeadChannel[]).map((c) => (
                  <MenuItem key={c} value={c}>
                    {LEAD_CHANNEL_LABELS[c]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                }}
              >
                <TableCell>Nome</TableCell>
                <TableCell>WhatsApp</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Veículo interesse</TableCell>
                <TableCell>Último contato</TableCell>
                <TableCell>Próximo contato</TableCell>
                <TableCell align="center">Dias s/ contato</TableCell>
                <TableCell>Alerta</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {items.length === 0 ? 'Nenhum lead cadastrado.' : 'Nenhum resultado para os filtros.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((lead) => {
                  const days = calcDaysWithoutContact(lead);
                  const alert = resolveLeadAlert(lead);
                  const sellerName =
                    lead.seller?.name ||
                    (lead.seller_id != null ? sellerMap.get(lead.seller_id) : null) ||
                    '—';
                  const waMsg = buildLeadWhatsappMessage(lead);
                  return (
                    <TableRow
                      key={lead.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setOverviewLead(lead)}
                    >
                      <TableCell>
                        <Typography fontWeight={600}>{lead.name}</Typography>
                        {lead.city_neighborhood ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {lead.city_neighborhood}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{formatPhoneBR(lead.whatsapp)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={LEAD_STATUS_LABELS[lead.status] ?? lead.status}
                          color={LEAD_STATUS_COLORS[lead.status] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{sellerName}</TableCell>
                      <TableCell>{lead.vehicle_interest || '—'}</TableCell>
                      <TableCell>{formatDateBR(lead.last_contact_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDateBR(lead.next_contact_at)}</Typography>
                        {lead.next_contact_action ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {lead.next_contact_action}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">{days == null ? '—' : days}</TableCell>
                      <TableCell>
                        {alert === 'nenhum' ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Chip
                            size="small"
                            label={LEAD_ALERT_LABELS[alert]}
                            color={LEAD_ALERT_COLORS[alert]}
                            variant="filled"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => setOverviewLead(lead)}
                          >
                            Ver
                          </Button>
                          {canWrite ? (
                            <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(lead)}>
                              Editar
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            color="success"
                            startIcon={<WhatsAppIcon />}
                            component="a"
                            href={getWhatsappLink(onlyDigits(lead.whatsapp), waMsg)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            WhatsApp
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <LeadFormModal
          open={formOpen}
          onClose={() => {
            if (saving) return;
            setFormOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          lead={editing}
          sellers={sellers}
          saving={saving}
        />

        <LeadOverviewDialog
          open={Boolean(overviewLead)}
          lead={overviewLead}
          sellerName={
            overviewLead
              ? overviewLead.seller?.name || sellerNameById(overviewLead.seller_id)
              : undefined
          }
          canWrite={canWrite}
          onClose={() => setOverviewLead(null)}
          onEdit={openEdit}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3500}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setSnackbarOpen(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="success" variant="filled" sx={{ borderRadius: 2 }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default LeadsList;
